/**
 * Announcements API client — CVG3-NOTIF (L104 addendum, 2026-07-11).
 *
 * The announcement IS the tenant-config `homeThought.pin` (CVG2), grown with
 * `placement: 'pre_login' | 'post_login' | 'both'`. This client PUTs ONLY
 * `{ homeThought: { pin } }` (scoped payload — same pattern as the CVG2
 * editor; the server deep-merge leaves `quotes` and every other config key
 * untouched). Do NOT put the full merged config through this route — the
 * backend schema is `.strict()` (known CVG2 bottom-bar-Save flag).
 */
import { api } from '@/utils/api';

export type AnnouncementPlacement = 'pre_login' | 'post_login' | 'both';

export interface AnnouncementPin {
  message: string;
  endAt: string | null; // ISO or null = never expires
  dismissable: boolean;
  placement: AnnouncementPlacement;
}

export function normalizeAnnouncementPlacement(v: unknown): AnnouncementPlacement {
  return v === 'pre_login' || v === 'both' ? v : 'post_login';
}

/** Current pin from the merged tenant config; null = no active announcement. */
export async function fetchAnnouncementPin(): Promise<AnnouncementPin | null> {
  const res = await api.get('/admin/tenant-config');
  const pin = res.data?.data?.homeThought?.pin;
  if (!pin?.message) return null;
  return {
    message: pin.message,
    endAt: pin.endAt ?? null,
    dismissable: pin.dismissable !== false,
    placement: normalizeAnnouncementPlacement(pin.placement),
  };
}

/**
 * Save (or clear with null) the announcement pin. Scoped PUT — only the
 * `homeThought.pin` subtree travels. A NEW/CHANGED message re-shows the pin
 * to reps who dismissed the old one AND (for post_login/both) fires the
 * hot-prominence phone ping server-side.
 */
export async function saveAnnouncementPin(pin: AnnouncementPin | null): Promise<void> {
  await api.put('/admin/tenant-config', {
    homeThought: {
      pin: pin
        ? {
            message: pin.message,
            endAt: pin.endAt,
            dismissable: pin.dismissable,
            placement: pin.placement,
          }
        : null,
    },
  });
}
